import React, { useEffect, useState } from 'react';
import { Link } from 'react-scroll';
import { cn } from '../lib/cn';

const SECTIONS = [
  { id: 'za_nas', numKey: '01', key: 'about' },
  { id: 'uslugi', numKey: '02', key: 'services' },
  { id: 'galeriya', numKey: '03', key: 'gallery' },
  { id: 'kontakti', numKey: '04', key: 'contact' },
];

export const Navigation = ({
  data,
  switchLanguage,
  currentLanguage,
  onRequestQuote,
  quoteLabel,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const labelFor = (key) => data?.[key] || key;
  const wordmark = currentLanguage === 'bg' ? 'ДВ Пласт' : 'DV Plast';

  const handleQuoteClick = () => {
    setIsOpen(false);
    onRequestQuote?.();
  };

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-40 transition-[background-color,backdrop-filter,border-color] duration-500 ease-out-quart',
          scrolled
            ? 'bg-paper/85 backdrop-blur-md border-b border-rule'
            : 'bg-transparent border-b border-transparent'
        )}
      >
        <div className="max-wrap section-wrap">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link
              to="header"
              smooth
              duration={600}
              className="group inline-flex items-center gap-3 cursor-pointer"
              aria-label="DV Plast — home"
            >
              <img
                src={data?.logo || '/img/favicon.webp'}
                alt=""
                className={cn(
                  'w-8 h-8 rounded-full object-cover transition-all duration-500',
                  scrolled ? 'opacity-100' : 'opacity-95 ring-1 ring-paper/30'
                )}
                width="32"
                height="32"
              />
              <span
                className={cn(
                  'font-serif text-lg sm:text-xl tracking-editorial transition-colors duration-500',
                  scrolled ? 'text-ink' : 'text-paper'
                )}
              >
                {wordmark}
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-10">
              {SECTIONS.map((s) => (
                <Link
                  key={s.id}
                  to={s.id}
                  smooth
                  duration={600}
                  spy
                  offset={-60}
                  className={cn(
                    'group relative inline-flex items-baseline gap-2 cursor-pointer transition-colors duration-300',
                    scrolled ? 'text-ink hover:text-brass-deep' : 'text-paper hover:text-paper'
                  )}
                  activeClass="!text-brass-deep"
                >
                  <span
                    className={cn(
                      'font-mono text-[0.65rem] tabular-nums tracking-widest transition-colors duration-300',
                      scrolled ? 'text-ink-muted' : 'text-paper/55'
                    )}
                  >
                    {s.numKey}
                  </span>
                  <span className="text-sm font-medium tracking-wide">{labelFor(s.key)}</span>
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-5">
              <div
                className={cn(
                  'flex items-center gap-1 font-mono text-[0.72rem] tracking-[0.18em] transition-colors duration-500',
                  scrolled ? 'text-ink-muted' : 'text-paper/70'
                )}
                role="group"
                aria-label="Language"
              >
                <button
                  onClick={() => switchLanguage('bg')}
                  className={cn(
                    'px-2 py-1 uppercase transition-colors duration-300',
                    currentLanguage === 'bg'
                      ? scrolled ? 'text-ink' : 'text-paper'
                      : 'hover:text-brass-deep'
                  )}
                  aria-pressed={currentLanguage === 'bg'}
                >
                  BG
                </button>
                <span aria-hidden="true">/</span>
                <button
                  onClick={() => switchLanguage('en')}
                  className={cn(
                    'px-2 py-1 uppercase transition-colors duration-300',
                    currentLanguage === 'en'
                      ? scrolled ? 'text-ink' : 'text-paper'
                      : 'hover:text-brass-deep'
                  )}
                  aria-pressed={currentLanguage === 'en'}
                >
                  EN
                </button>
              </div>

              {onRequestQuote && quoteLabel && (
                <button
                  type="button"
                  onClick={handleQuoteClick}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-xs font-sans text-[0.72rem] tracking-[0.18em] uppercase transition-colors duration-300',
                    scrolled
                      ? 'bg-ink text-paper hover:bg-brass-deep'
                      : 'bg-paper text-ink hover:bg-brass-soft'
                  )}
                >
                  {quoteLabel}
                </button>
              )}
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              className={cn(
                'lg:hidden relative z-50 inline-flex items-center justify-center w-11 h-11 -mr-2 transition-colors duration-300',
                isOpen ? 'text-ink' : scrolled ? 'text-ink' : 'text-paper'
              )}
            >
              <span className="sr-only">Menu</span>
              <span className="relative flex flex-col gap-1.5">
                <span
                  className={cn(
                    'block h-px w-6 bg-current transition-transform duration-300',
                    isOpen && 'translate-y-[7px] rotate-45'
                  )}
                />
                <span
                  className={cn(
                    'block h-px w-6 bg-current transition-opacity duration-300',
                    isOpen && 'opacity-0'
                  )}
                />
                <span
                  className={cn(
                    'block h-px w-6 bg-current transition-transform duration-300',
                    isOpen && '-translate-y-[7px] -rotate-45'
                  )}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      <div
        className={cn(
          'lg:hidden fixed inset-0 z-30 bg-paper transition-[opacity,transform] duration-500 ease-out-quart',
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
        )}
        aria-hidden={!isOpen}
      >
        <div className="h-full flex flex-col pt-24 pb-10 px-6 sm:px-10">
          <nav className="flex-1 flex flex-col justify-center gap-2">
            {SECTIONS.map((s, i) => (
              <Link
                key={s.id}
                to={s.id}
                smooth
                duration={600}
                onClick={() => setIsOpen(false)}
                className="group flex items-baseline gap-5 py-4 border-b border-rule cursor-pointer"
                style={{ transitionDelay: isOpen ? `${i * 60}ms` : '0ms' }}
              >
                <span className="font-mono text-xs tabular-nums tracking-widest text-ink-muted w-8">
                  {s.numKey}
                </span>
                <span className="font-serif text-3xl sm:text-4xl tracking-editorial text-ink group-hover:text-brass-deep transition-colors duration-300">
                  {labelFor(s.key)}
                </span>
              </Link>
            ))}
          </nav>

          {onRequestQuote && quoteLabel && (
            <button
              type="button"
              onClick={handleQuoteClick}
              className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-ink text-paper px-6 py-4 rounded-xs hover:bg-brass-deep transition-colors duration-300"
            >
              <span className="font-sans text-sm tracking-[0.18em] uppercase">{quoteLabel}</span>
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-px overflow-visible text-paper" aria-hidden="true">
                <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M0 12h24m0 0l-6-6m6 6l-6 6" />
              </svg>
            </button>
          )}

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.2em] text-ink-muted">
              <button
                onClick={() => { switchLanguage('bg'); }}
                className={cn('uppercase px-2 py-1 transition-colors', currentLanguage === 'bg' ? 'text-ink' : 'hover:text-brass-deep')}
                aria-pressed={currentLanguage === 'bg'}
              >
                Български
              </button>
              <span aria-hidden="true">/</span>
              <button
                onClick={() => { switchLanguage('en'); }}
                className={cn('uppercase px-2 py-1 transition-colors', currentLanguage === 'en' ? 'text-ink' : 'hover:text-brass-deep')}
                aria-pressed={currentLanguage === 'en'}
              >
                English
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
